import { useState } from 'react';
import {
    MRT_EditActionButtons,
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    QueryClient,
    QueryClientProvider,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';


const Table = ({
                   columns,
                   createApi,
                   readApi,
                   updateApi,
                   deleteApi,
                   itemName,
                   columnVisibility,
                   validationErrors,
                   setValidationErrors,
                   publicSitePage
               }) => {
    const queryClient = useQueryClient();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRowToDelete, setSelectedRowToDelete] = useState(null);

    const validateRowFromColumns = (columns, values) => {
        const errors = {};
        columns.forEach((col) => {
            if (typeof col.accessorKey === 'string' && typeof col.validate === 'function') {
                const error = col.validate(values[col.accessorKey], values); // pass full row as 2nd arg
                if (error) {
                    errors[col.accessorKey] = error; // use `accessorKey` here, not `col.id`
                }
            }
        });
        return errors;
    };

    const { mutateAsync: createData, isPending: isCreating } = useMutation({
        mutationFn: async (newData) => {
            //console.log(newData.id)
            if (!newData.id) newData.id = 0;

            const response = await fetch(createApi, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(newData),
            });

            if (!response.ok) throw new Error('Failed to create data');

            return response.json();
        },
        onSuccess: (createdItem) => {
            queryClient.setQueryData(['data'], (oldData = []) => [...oldData, createdItem]);
        },
        onError: (error) => {
            console.error('Create error:', error);
        },
    });

    const {
        data: fetchedData = [],
        isError: isErrorFetching,
        isFetching,
    } = useQuery({
        queryKey: ['data'],
        queryFn: async () => {
            const response = await fetch(readApi);
            if (!response.ok) throw new Error('Failed to fetch');
            return response.json();
        },
        refetchOnWindowFocus: false,
    });

    const { mutateAsync: updateData, isPending: isUpdating } = useMutation({
        mutationFn: async (updatedData) => {
            const response = await fetch(updateApi, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(updatedData),
            });
            //console.log(updatedData);
            if (!response.ok) throw new Error('Failed to update');

            return response.json();
        },
        onSuccess: (updatedItem) => {
            queryClient.setQueryData(['data'], (oldData = []) =>
                oldData.map((item) => (item.id === updatedItem.id ? { ...item, ...updatedItem } : item))
            );
        },
        onError: (error) => {
            console.error('Update error:', error);
        },
    });

    const { mutateAsync: deleteData, isPending: isDeleting } = useMutation({
        mutationFn: async (id) => {
            const response = await fetch(`${deleteApi}/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType?.includes('application/json')) {
                    const errData = await response.json();
                    throw new Error(errData.message || 'Failed to delete');
                }
                throw new Error('Failed to delete');
            }

            return id;
        },
        onMutate: async (id) => {
            await queryClient.cancelQueries(['data']);
            const previousData = queryClient.getQueryData(['data']);
            queryClient.setQueryData(['data'], (oldData) =>
                oldData?.filter((item) => item.id !== id)
            );
            return { previousData };
        },
        onError: (err, id, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(['data'], context.previousData);
            }
        },
    });

    const handleCreate = async ({ values, table }) => {
        const errors = validateRowFromColumns(columns, values);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        if (!values.id) values.id = 0;

        const formData = new FormData();

        // If there's a file
        if (values.imageFile) {
            formData.append("Image", values.imageFile);
        }

        // Append all other values
        Object.entries(values).forEach(([key, value]) => {
            if (key !== "imageFile" && value !== undefined && value !== null) {
                formData.append(key, value);
            }
        });

        const response = await fetch(createApi, {
            method: "POST",
            credentials: "include",
            body: formData, // no content-type header here
        });

        if (!response.ok) throw new Error("Failed to create");

        const createdItem = await response.json();
        queryClient.setQueryData(["data"], (oldData = []) => [...oldData, createdItem]);

        table.setCreatingRow(null);
    };


    const handleSave = async ({ values, table }) => {
        const errors = validateRowFromColumns(columns, values);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        const formData = new FormData();

        if (values.imageFile) {
            formData.append("Image", values.imageFile);
        }

        Object.entries(values).forEach(([key, value]) => {
            if (key !== "imageFile" && value !== undefined && value !== null) {
                formData.append(key, value);
            }
        });

        const response = await fetch(updateApi, {
            method: "POST",
            credentials: "include",
            body: formData, // no content-type header here
        });

        // Read the body once
        let responseBody;
        try {
            responseBody = await response.clone().json(); // clone allows us to read it safely
        } catch {
            try {
                responseBody = await response.clone().text(); // fallback to text
            } catch {
                responseBody = "Could not read response body";
            }
        }

// Throw an error if not OK
        if (!response.ok) {
            throw new Error(
                `Failed to update. Status: ${response.status} ${response.statusText}. Response: ${JSON.stringify(responseBody)}`
            );
        }

        const updatedItem = await response.json();
        queryClient.setQueryData(["data"], (oldData = []) =>
            oldData.map((item) => (item.id === updatedItem.id ? { ...item, ...updatedItem } : item))
        );

        table.setEditingRow(null);
    };


    const openDeleteConfirmModal = (row) => {
        setSelectedRowToDelete(row);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedRowToDelete) {
            await deleteData(selectedRowToDelete.original.id);
            setSelectedRowToDelete(null);
            setDeleteDialogOpen(false);
        }
    };

    const handleCancelDelete = () => {
        setSelectedRowToDelete(null);
        setDeleteDialogOpen(false);
    };

    const table = useMaterialReactTable({
        columns,
        data: fetchedData,
        validationErrors,
        createDisplayMode: 'modal',
        editDisplayMode: 'modal',
        enableEditing: true,
        getRowId: (row) => row.id,
        initialState: {
            columnVisibility: columnVisibility ?? {},
            density: 'compact', // makes rows less tall for a cleaner look
        },
        onCreatingRowSave: handleCreate,
        onEditingRowSave: handleSave,
        onCreatingRowCancel: () => setValidationErrors({}),
        onEditingRowCancel: () => setValidationErrors({}),
        muiEditRowDialogProps: {
            fullWidth: true,
            maxWidth: false,
            PaperProps: {
                sx: {
                    width: '1000px',
                    maxWidth: '100%',
                    margin: 'auto',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                },
            },
        },
        muiTableContainerProps: {
            sx: {
                minHeight: '500px',
                borderRadius: '0.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden',
            },
        },
        muiTableHeadCellProps: {
            sx: {
                fontWeight: 'bold',
                color: '#1f2937',
                fontSize: '1.1rem',
                fontFamily: `"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif`
            },
        },
        muiTableBodyCellProps: {
            sx: {
                padding: '8px 12px',
                fontSize: '1.3rem',
                fontFamily: `"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif`
            },
        },
        muiTableBodyProps: {
            sx: {
                '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                '& tr:hover': { backgroundColor: '#f1f5f9' },
            },
        },
        muiToolbarAlertBannerProps: isErrorFetching
            ? { color: 'error', children: 'Error loading data' }
            : undefined,
        renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
            <>
                <DialogTitle>Create New {itemName}</DialogTitle>
                <DialogContent dividers>{internalEditComponents}</DialogContent>
                <DialogActions>
                    <MRT_EditActionButtons variant="text" table={table} row={row} />
                </DialogActions>
            </>
        ),
        renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
            <>
                <DialogTitle>Edit {itemName}</DialogTitle>
                <DialogContent dividers>{internalEditComponents}</DialogContent>
                <DialogActions>
                    <MRT_EditActionButtons variant="text" table={table} row={row} />
                </DialogActions>
            </>
        ),
        renderRowActions: ({ row, table }) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => table.setEditingRow(row)}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 1,
                        fontWeight: 600,
                        px: 1.5,
                        ':hover': {
                            backgroundColor: 'primary.main',
                            color: 'common.white',
                            '& .MuiSvgIcon-root': {
                                color: 'common.white',
                            },
                        },
                    }}
                >
                    Edit
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => openDeleteConfirmModal(row)}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 1,
                        fontWeight: 600,
                        px: 1.5,
                        ':hover': {
                            backgroundColor: 'error.main',
                            color: 'common.white',
                            '& .MuiSvgIcon-root': {
                                color: 'common.white',
                            },
                        },
                    }}
                >
                    Delete
                </Button>
            </Box>
        ),
        renderTopToolbarCustomActions: ({ table }) => (
            <Box sx={{ height: '64px', display: 'flex', alignItems: 'center', px: 2, gap: 2 }}>
                {/* Create New button */}
                <Button
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<AddIcon />}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: '600',
                        boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            boxShadow: '0 5px 12px rgba(0,0,0,0.3)',
                            backgroundColor: 'error.dark',
                        },
                    }}
                    onClick={() => table.setCreatingRow(true)}
                >
                    Create New {itemName}
                </Button>

                {/* View button only if publicSitePage is provided */}
                {publicSitePage && (
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<VisibilityIcon />}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: '600',
                            backgroundColor: '#FF7F50',
                            color: 'white',
                            boxShadow: '0 3px 8px rgba(255,127,80,0.5)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#FF6347',
                                boxShadow: '0 5px 12px rgba(255,99,71,0.7)',
                            },
                        }}
                        onClick={() => {
                            const baseUrl = "https://www.federationskatingclub.ca/";
                            const fullUrl = new URL(publicSitePage, baseUrl).toString();
                            window.open(fullUrl, '_blank', 'noopener,noreferrer');
                        }}
                    >
                        View Page
                    </Button>
                )}
            </Box>
        ),
        state: {
            isLoading: isFetching,
            isSaving: isCreating || isUpdating || isDeleting,
            showAlertBanner: isErrorFetching,
        },
    });

    return (
        <>
            <MaterialReactTable table={table} />
            <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent dividers>
                    Are you sure you want to delete this {itemName}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const queryClient = new QueryClient();

const GenericTable = (props) => (
    <QueryClientProvider client={queryClient}>
        <Table {...props} />
    </QueryClientProvider>
);

export default GenericTable;
